import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SmartLink from '../common/SmartLink'
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import logoImg from '../assets/logo.jpeg';
import heroImgLocal from '../assets/hero.png';
import { supabase } from '../lib/supabaseClient';
import { defaultContent } from '../lib/defaultContent';
import { useRegisterEditor } from '../lib/EditorContext';

const { FiArrowRight, FiBookOpen, FiUsers, FiVideo, FiPlayCircle, FiHeart, FiSmile, FiStar } = FiIcons;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

export default function Home() {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from('page_content')
          .select('content')
          .eq('page_id', 'home')
          .single();

        if (error) {
          console.warn('Could not fetch home content, using defaults.', error.message);
        } else if (data && data.content) {
          setContent(data.content);
        }
      } catch (err) {
        console.error('Error fetching home content:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContent();
  }, []);

  const miniLogoText = content?.hero?.miniLogoText || defaultContent.home.hero.miniLogoText;
  const miniLogoImage = content?.hero?.miniLogoImage || defaultContent.home.hero.miniLogoImage;
  const title = content?.hero?.title || defaultContent.home.hero.title;
  const subtitlePrefix = content?.hero?.subtitlePrefix || defaultContent.home.hero.subtitlePrefix;
  const bullets = content?.hero?.bullets || defaultContent.home.hero.bullets;
  const subtitleSuffix = content?.hero?.subtitleSuffix || defaultContent.home.hero.subtitleSuffix;
  const heroImg = content?.hero?.heroImg || defaultContent.home.hero.heroImg || heroImgLocal;

  const parentingStrategiesButtonText = content?.hero?.parentingStrategiesButtonText || defaultContent.home.hero.parentingStrategiesButtonText;
  const parentingStrategiesButtonLink = content?.hero?.parentingStrategiesButtonLink || defaultContent.home.hero.parentingStrategiesButtonLink;
  const coachingButtonText = content?.hero?.coachingButtonText || defaultContent.home.hero.coachingButtonText;
  const coachingButtonLink = content?.hero?.coachingButtonLink || defaultContent.home.hero.coachingButtonLink;

  const badgeText = content?.badge?.text || defaultContent.home.badge.text;
  const badgeSubtext = content?.badge?.subtext || defaultContent.home.badge.subtext;

  const servicesTitle = content?.servicesTitle || defaultContent.home.servicesTitle;
  const servicesSubtitle = content?.servicesSubtitle || defaultContent.home.servicesSubtitle;
  const reviewsTitle = content?.reviewsTitle || defaultContent.home.reviewsTitle;
  const reviewsSubtitle = content?.reviewsSubtitle || defaultContent.home.reviewsSubtitle;

  const services = content?.services || defaultContent.home.services;

  const currentReviews = content?.reviews || defaultContent.home.reviews;

  const handleSaveSuccess = (newContent) => {
    setContent(newContent);
  };

  useRegisterEditor(
    'home',
    { hero: { miniLogoText, miniLogoImage, heroImg, title, subtitlePrefix, bullets, subtitleSuffix, parentingStrategiesButtonText, parentingStrategiesButtonLink, coachingButtonText, coachingButtonLink }, badge: { text: badgeText, subtext: badgeSubtext }, servicesTitle, servicesSubtitle, reviewsTitle, reviewsSubtitle, services, reviews: currentReviews },
    handleSaveSuccess,
    'Home Page'
  );

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" exit="hidden">
      {/* Hero Section */}
      <section className="relative pt-10 pb-20 lg:pt-24 lg:pb-32 overflow-hidden">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[600px] h-[600px] bg-brand-rose/10 rounded-full blur-3xl -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-rose text-white mb-6">
                <img 
                  src={miniLogoImage}
                  className="w-5 h-5 rounded-full border border-brand-rose"
                  alt="mini-logo"
                />
                <span className="text-xs font-bold uppercase tracking-wider">{miniLogoText} </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl  font-bold leading-[1.1] mb-8 text-[#333] dark:text-[#fff]">
                {title}
              </h1>
              <div className="text-xl mb-10 leading-relaxed font-light">
                <p className="mb-4">{subtitlePrefix}</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  {bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
                <p className="font-medium">{subtitleSuffix}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-5">
                <SmartLink to={parentingStrategiesButtonLink} className="px-10 py-4 bg-brand-rose text-white rounded-full font-medium text-center hover:brightness-110 transition-all shadow-[2px_2px_0px_0px_rgba(45,55,72,1)] border-2 border-brand-darkGray hover:translate-y-1 hover:shadow-none flex items-center justify-center gap-2">
                  <SafeIcon icon={FiPlayCircle} /> {parentingStrategiesButtonText}
                </SmartLink>
                <SmartLink to={coachingButtonLink} className="px-10 py-4 bg-white dark:bg-brand-slate border border-sand-200 rounded-full font-medium text-center hover:bg-sand-50 dark:bg-brand-slate/80 transition-all flex items-center justify-center gap-2 group">
                  {coachingButtonText} <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                </SmartLink>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="relative">
              <div className="absolute -bottom-6 -left-6 bg-white dark:bg-brand-slate p-6 rounded-2xl shadow-xl z-20 hidden md:block">
                <p className="  font-bold text-sm mb-1">{badgeText}</p>
                <p className="  text-xs">{badgeSubtext}</p>
              </div>
              <img 
                src={heroImg}
                alt="Whimsical family moments"
                className="rounded-[3rem] shadow-2xl object-cover h-[600px] w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-white dark:bg-brand-slate bg-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl  font-bold mb-6 text-[#333] dark:text-[#fff]">{servicesTitle}</h2>
            <p className="text-lg">{servicesSubtitle}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => {
              const bgColors = ['bg-orange-300', 'bg-yellow-300', 'bg-green-300', 'bg-blue-300', 'bg-purple-300', 'bg-pink-300'];
              const textColors = ['text-orange-800', 'text-yellow-800', 'text-green-800', 'text-blue-800', 'text-purple-800', 'text-pink-800'];
              const IconComponent = FiIcons[service.iconName] || FiIcons.FiStar;

              return (
              <motion.div key={idx} variants={fadeUp} className={`${bgColors[idx % 6]} ${textColors[idx % 6]} rounded-[2rem] p-8 transition-all group transform hover:scale-105 shadow-lg`}>
                <div className="bg-white/40 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-sm transition-all">
                  <SafeIcon icon={IconComponent} className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-sm mb-6 leading-relaxed opacity-90">{service.desc}</p>
                <SmartLink to={service.link} className="font-bold text-xs uppercase tracking-widest flex items-center gap-2 group-hover:gap-3 transition-all cursor-pointer">
                  Learn More <SafeIcon icon={FiArrowRight} />
                </SmartLink>
              </motion.div>
            )})}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-brand-rose/5 dark:bg-brand-slate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl font-bold mb-6 text-[#333] dark:text-[#fff]">{reviewsTitle}</h2>
            <p className="text-lg text-[#333] dark:text-[#fff]">{reviewsSubtitle}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentReviews.map((review, idx) => (
              <motion.div key={idx} variants={fadeUp} className="bg-white dark:bg-brand-slate p-8 rounded-[2rem] shadow-lg border border-sand-100 flex flex-col h-full hover:scale-105 transition-transform duration-300">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-[#333] dark:text-[#fff] italic mb-6 flex-grow">"{review.text}"</p>
                <div className="flex items-center gap-4 mt-auto">
                  <div className="w-12 h-12 bg-brand-rose text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {review.initials}
                  </div>
                  <div>
                    <h4 className="font-bold text-[#333] dark:text-[#fff]">{review.initials}</h4>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
}